/* eslint-disable no-nested-ternary, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */
/* istanbul ignore file */
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/core';
import axios, { AxiosError } from 'axios';
import Joi from 'joi';
import _findIndex from 'lodash/findIndex';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import _omit from 'lodash/omit';
import 'rc-steps/assets/index.css';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiInfoCircle } from 'react-icons/bi';
import { useParams } from 'react-router';
import { ComplianceContext, PositionComplianceReminder } from '../../../../app/components/ComplianceReminder';
import useBackgroundChecks from '../../../../app/hooks/useBackgroundChecks';
import useContactEmailForAccountAuthorisation from '../../../../app/hooks/useContactEmailForAccountAuthorisation';
import useLookupsByType from '../../../../app/hooks/useLookupsByType';
import { AUDIO_EVALUATION_OPTIONS } from '../../../../firebase/firestore/constants';
import { DialogFlowAgent } from '../../../../firebase/firestore/documents/accounts';
import { BackgroundChecks } from '../../../../firebase/firestore/documents/candidate';
import { LanguageEvaluationPhrases } from '../../../../firebase/firestore/documents/languageEvaluationPhrases';
import { LanguageMap } from '../../../../firebase/firestore/documents/user';
import { RemoteConfigKey } from '../../../../firebase/remoteConfig';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import { clearErrorMessage, errorFormat, FormattedError, updateObject } from '../../../../utils/FormErrorUtils';
import { getTranslatedValue } from '../../../../utils/localizedString';
import { sanitizeOfferConditions } from '../../../../utils/uiUtils';
import { Business } from '../BusinessType';
import { usePositionManagementContext } from '../PositionManagementContext';
import {
  FreeFormQuestion,
  IndustryType,
  Interview,
  JobPosition,
  JobType,
  JopPositionResponse,
  MultipleChoiceQuestion,
  OccupationType,
  Question,
  Schedule,
  SkillForPosition,
} from '../PositionType';
import useBusinessLocations from '../useBusinessLocations';
import { AdvanceFieldsCard } from './AdvanceFieldsCard';
import { AutomationCard } from './AutomationCard';
import { AutomationTypes } from './AutomationTypes';
import { CertnBackgroundCheckCard } from './CertnBackgroundCheckCard';
import { createJobPositionStyle } from './CreateJobPositionStyle';
import { FreeFormQuestionsCard } from './FreeFormQuestionsCard';
import { handleHireFormErrors } from './HireFormTemplateSelect';
import { InterviewCard } from './InterviewCard';
import { JobPositionDetailCard, rangeRequiredDisplayOptions, SalaryDisplayOption, salaryOptions } from './JobPositionDetailCard';
import { PositionAvailabilityCard } from './PositionAvailabilityCard';
import { QuestionsView } from './QuestionsView';
import { ReviewCreateJobView } from './ReviewCreateJobView';
import { SpecificQuestionsCard } from './SpecificQuestionsCard';
import { VideoDetailCard } from './VideoDetailCard';
import { usePositionParametersFormFields } from './positionParameter/usePositionParametersFormFields';
import PositionParametersView from './positionParameter/PositionParametersView';
import { QuestionnaireTemplateListResponse } from '../../questionnaireTemplate/questionnaireTemplate.types';

const initialBackgroundCheck = {
  request_instant_verify_education: false,
  request_instant_verify_employment: false,
  request_enhanced_identity_verification: false,
  request_criminal_record_check: false,
  request_enhanced_criminal_record_check: false,
  request_us_criminal_record_check_tier_1: false,
  request_us_criminal_record_check_tier_2: false,
  request_soquij: false,
  request_right_to_work: false,
};

const initialQuestion = {
  text: '',
  preferredAnswer: true,
  knockout: false,
  isPausable: false,
};

const initialFreeFormQuestion = {
  text: '',
  isActive: true,
  isPausable: false,
};

export type NewCreateJobsViewProps = {
  positionDataToEdit: JopPositionResponse | null;
  redirectToPositionAfterCreated: (jobPositionData: JobPosition, isCopyingPosition: boolean) => void;
  selectedBusiness: Business | null;
  onClose: (business: Business | null) => void;
  createNewPosition: boolean;
  disablePositionCreation?: boolean;
  showPositionCreationOnPage?: boolean;
  isCopyingPosition?: boolean;
  onCompleteCopy?: (business: Business | null) => void;
};

export const NewCreateJobsView = ({
  positionDataToEdit,
  redirectToPositionAfterCreated,
  selectedBusiness,
  onClose,
  createNewPosition,
  disablePositionCreation,
  showPositionCreationOnPage,
  isCopyingPosition,
  onCompleteCopy,
}: NewCreateJobsViewProps): JSX.Element => {
  const { businessId: businessIdFromParams } = useParams<{ businessId: string }>();
  const { t, i18n } = useTranslation();
  const FORM_FIELDS_CONFIG = usePositionParametersFormFields();
  const toast = useToast();
  const backgroundChecks = useBackgroundChecks();
  const businessLocations = useBusinessLocations();
  const audioEvaluationOptions = useLookupsByType(AUDIO_EVALUATION_OPTIONS);
  const contactEmailForAuthorization = useContactEmailForAccountAuthorisation();
  const { businessData, setPositionQueryParams, positionQueryParams } = usePositionManagementContext();

  const { user, accounts } = useStoreState((s) => s.app);
  const screeningOptions = useStoreState((state) => state.app.accounts?.screeningOptions);
  const isWorkbrightEnabled = useStoreState((state) => state.app.accounts?.configuration?.workbright?.enabled);
  const isCertnEnabled = useStoreState((state) => state.app.accounts?.configuration?.certnEnabled);
  const isSmbRecruiter = useStoreState((state) => state.app.accounts?.smb);
  const isRequisitionEnabled = useStoreState((state) => state.app.accounts?.configuration?.requisitionEnabled);
  const isHireFormTemplateEnabled = useStoreState((s) => s.app.accounts?.configuration?.hireFormTemplateEnabled);
  const isLETemplateEnabled = useStoreState((s) => s.app.accounts?.configuration?.languageEvaluationTemplateEnabled);
  const isFitbandTemplateEnabled = useStoreState((s) => s.app.accounts?.configuration?.fitbandTemplateEnabled);
  const positionComplianceCheckedDate = useStoreState((s) => s.app.user?.positionComplianceCheckedDate);
  const hireFormEnabledPositions = useStoreState((s) => s.app.accounts?.configuration?.hireFormEnabledPositions);
  const isCertnBackgroundEnabled = useStoreState((s) => s.app.accounts?.configuration?.certnEnabled);
  const isQuestionTemplateEnabled = useStoreState((s) => s.app.accounts?.configuration?.questionTemplateEnabled);

  const enableMultipleChoiceFeature: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_MULTIPLE_CHOICE_FEATURE];
  });
  const enableToggleOfBackgroundCheckOnPositionLevel: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_TOGGLE_OF_BACKGROUND_CHECK_ON_POSITION_LEVEL];
  });

  const accountRequisitionActiveThresholdDays = useStoreState(
    (state) => state.app.accounts?.configuration?.requisitionActiveThresholdDays,
  );

  const initialSalaryData = {
    customUnit: null,
    maxValue: '',
    minValue: '',
    unit: 'hourly',
  };

  const initialInterviewData = {
    interviewType: 'video',
    interviewDuration: 30,
    interviewMessage: '',
    interviewLocation: '',
  };

  const initialWorkSchedule = {
    sun: { day: '', evening: '', night: '' },
    mon: { day: 'AM_PM', evening: '', night: '' },
    tue: { day: 'AM_PM', evening: '', night: '' },
    wed: { day: 'AM_PM', evening: '', night: '' },
    thu: { day: 'AM_PM', evening: '', night: '' },
    fri: { day: 'AM_PM', evening: '', night: '' },
    sat: { day: '', evening: '', night: '' },
  } as { [index: string]: Schedule };

  const DEFAULT_LANGUAGE_EVALUATION_PASS_THRESHOLD = 2;

  const getScreeningOptionData = () => {
    const newOptions: BackgroundChecks = initialBackgroundCheck;
    backgroundChecks.forEach((bc) => {
      newOptions[bc.id as keyof BackgroundChecks] =
        !_isNil(screeningOptions) && !_isNil(positionDataToEdit) && !_isNil(positionDataToEdit?.screeningOptions)
          ? screeningOptions[bc.id as keyof BackgroundChecks] &&
            positionDataToEdit.screeningOptions[bc.id as keyof BackgroundChecks]
          : false;
    });
    // eslint-disable-next-line no-nested-ternary
    return isCertnEnabled ? (positionDataToEdit ? newOptions : screeningOptions) : null;
  };

  const initialJobPositionData = {
    positionId: '',
    automated: false,
    automationType: null,
    industryName: '',
    industry: '',
    occupation: '',
    job: '',
    jobCustomName: '',
    business: '',
    businessName: '',
    businessAddress: '',
    noOfOpenings: null,
    positionType: '',
    salaryDisplayOption: SalaryDisplayOption.STARTING_AT,
    proposedSalary: initialSalaryData,
    experienceFilterType: null,
    requiredExperience: null,
    jobDescription: '',
    recruiterNote: positionDataToEdit?.recruiterNotes
      ? positionDataToEdit?.recruiterNotes.filter((item) => item.id === 'default')[0]?.notes
      : undefined,
    fullTime: true,
    interview: initialInterviewData,
    specificQuestions: ([] as unknown) as Question[],
    freeFormQuestions: ([] as unknown) as FreeFormQuestion[],
    multipleChoiceQuestions: ([] as unknown) as MultipleChoiceQuestion[],
    schedule: initialWorkSchedule,
    skills: [],
    enableHireFormSolution: false,
    hireFormTemplateId: null,
    modernHireEnable: false,
    modernHirePassThreshold: 0,
    modernHireTemplateId: '',
    enableScheduleMatch: false,
    fitBandTemplateEnable: false,
    fitBandTemplateId: '',
    virtualRecruiterEnabled: false,
    screeningOptions: getScreeningOptionData(),
    enableScreeningOptions: isCertnEnabled ? positionDataToEdit?.enableScreeningOptions ?? true : false,
    languageEvaluationPassThreshold: positionDataToEdit?.languageEvaluationPassThreshold
      ? positionDataToEdit?.languageEvaluationPassThreshold
      : DEFAULT_LANGUAGE_EVALUATION_PASS_THRESHOLD,
    leTemplateId: '',
    languageEvaluationEnable: !_isEmpty(positionDataToEdit?.leTemplateId),
    dialogFlowAgents: positionDataToEdit?.dialogFlowEnabled ? positionDataToEdit?.dialogFlowAgents?.slice(0, 1) ?? [] : undefined,
    dialogFlowEnabled: Boolean(positionDataToEdit?.dialogFlowEnabled),
    resumeEnabled: Boolean(positionDataToEdit?.resumeEnabled),
    offerConditions: positionDataToEdit?.offerConditions ?? undefined,
    i9Enabled: positionDataToEdit?.i9Enabled ? positionDataToEdit?.i9Enabled : isWorkbrightEnabled ?? false,
    questionTemplateId: positionDataToEdit?.questionTemplateId || null,
  };

  const [industries, setIndustries] = useState<IndustryType[]>([]);
  const [jobList, setJobList] = useState<JobType[]>([]);
  const [occupationList, setOccupationList] = useState<OccupationType[]>([]);
  const [jobPositionData, setJobPositionData] = useState<JobPosition>(initialJobPositionData);
  const [isInterviewAndBusinessLocationSame, setIsInterviewAndBusinessLocationSame] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFreeFormQuestion, setFreeFormQuestion] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<FormattedError[]>([]);
  const [salaryError, setSalaryError] = useState<FormattedError[]>([]);
  const jobPositionRef = useRef<null | HTMLDivElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [overviewVideoFile, setOverviewVideoFile] = useState<File | null>(null);
  const [overviewThumbnailFile, setOverviewThumbnailFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  const [isEnglishOfferEmpty, setIsEnglishOfferEmpty] = useState<boolean>(false);
  const [isTemplateUsed, setIsTemplateUsed] = useState<boolean>(false);
  const [selectedQuestionTemplate, setSelectedQuestionTemplate] = useState<QuestionnaireTemplateListResponse>();

  const { isOpen: isReviewOpened, onOpen: onOpenReview, onClose: onCloseReview } = useDisclosure();
  const { isOpen: isComplianceModalOpened, onOpen: onOpenComplianceModal, onClose: onCloseComplianceModal } = useDisclosure(); // useDisclosure for controlling modal state

  const isCountryAustralia = selectedBusiness?.country === 'Australia';

  const {
    getIndustries,
    getJobList,
    getOccupationList,
    createJobPosition,
    updateJobPosition,
    copyJobPosition,
    uploadOverviewVideo,
    uploadOverviewThumbnail,
    uploadOverviewVideoConfirmCall,
  } = useStoreActions((actions) => actions.positionManagement);

  const loadIndustriesJobsOccupation = useCallback(async () => {
    const industriesResponse = await getIndustries(null);
    const jobListResponse = await getJobList(null);
    const occupationListResponse = await getOccupationList(null);

    const allowedIndustryIds = [1, 2, 3, 9];
    const smbFilteredIndustries = industriesResponse.industries.filter((industry) => allowedIndustryIds.includes(industry.id));
    setIndustries(isSmbRecruiter ? smbFilteredIndustries : industriesResponse.industries);
    setOccupationList(occupationListResponse.occupations);
    setJobList(jobListResponse.jobs);
  }, [getIndustries, getJobList, getOccupationList, isSmbRecruiter]);

  const getIndustryName = (value: any) => {
    // eslint-disable-next-line radix
    return getTranslatedValue(industries.filter((industry) => industry.id === parseInt(value))[0].name, i18n.language);
  };

  const checkIfComplianceModalNeeded = (callback?: () => void) => {
    if (_isNil(positionComplianceCheckedDate)) {
      onOpenComplianceModal();
      return;
    }

    if (callback) {
      callback();
    }
  };

  const validateMaxGreaterThanMin = (value: string, helpers: Joi.CustomHelpers): Joi.ErrorReport | string => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { minValue } = helpers.state.ancestors[0];
    const min = parseFloat(minValue);
    const max = parseFloat(value);

    if (!Number.isNaN(min) && !Number.isNaN(max) && max <= min) {
      return helpers.message({
        custom: t('createJobPosition:salaryDisplayOptions.maxGreaterThanMin'),
      });
    }
    return value;
  };

  const interviewSchema = Joi.object().keys({
    interviewType: Joi.string()
      .required()
      .messages({
        'string.empty': t('createJobPosition:interviewTypeRequired'),
      }),
    interviewDuration: Joi.number()
      .required()
      .messages({
        'number.base': t('createJobPosition:interviewDurationRequired'),
      }),
    interviewMessage: Joi.string().trim(true).required().allow('').optional(),
    interviewLocation:
      jobPositionData.interview.interviewType === 'inPerson'
        ? Joi.string()
            .trim(true)
            .required()
            .messages({
              'string.empty': t('createJobPosition:interviewLocationRequired'),
            })
        : Joi.string().allow('').optional(),
  });

  const automationConfigurationSchema = Joi.object()
    .keys({
      target: Joi.when('...automationType', {
        is: 'screening',
        then: Joi.number()
          .min(1)
          .required()
          .messages({
            'number.base': t('createJobPosition:automationConfigurationRequired'),
            'number.min': t('createJobPosition:automationConfigurationMinRequired'),
          }),
        otherwise: Joi.number().allow(null).optional(),
      }),
      maxBookmarkAgeInDays: Joi.number()
        .allow(null)
        .min(FORM_FIELDS_CONFIG[1].min)
        .max(FORM_FIELDS_CONFIG[1].max)
        .optional()
        .label(FORM_FIELDS_CONFIG[1].key)
        .messages({
          'number.min': t('createJobPosition:mustBeBetweenError', {
            min: FORM_FIELDS_CONFIG[1].min,
            max: FORM_FIELDS_CONFIG[1].max,
          }),
          'number.max': t('createJobPosition:mustBeBetweenError', {
            min: FORM_FIELDS_CONFIG[1].min,
            max: FORM_FIELDS_CONFIG[1].max,
          }),
          'number.unsafe': t('createJobPosition:mustBeBetweenError', {
            min: FORM_FIELDS_CONFIG[1].min,
            max: FORM_FIELDS_CONFIG[1].max,
          }),
        }),
      overbookingFactor: Joi.number()
        .allow(null)
        .min(FORM_FIELDS_CONFIG[2].min)
        .max(FORM_FIELDS_CONFIG[2].max)
        .optional()
        .label(FORM_FIELDS_CONFIG[2].key)
        .messages({
          'number.min': t('createJobPosition:mustBeBetweenError', {
            min: FORM_FIELDS_CONFIG[2].min,
            max: FORM_FIELDS_CONFIG[2].max,
          }),
          'number.max': t('createJobPosition:mustBeBetweenError', {
            min: FORM_FIELDS_CONFIG[2].min,
            max: FORM_FIELDS_CONFIG[2].max,
          }),
          'number.unsafe': t('createJobPosition:mustBeBetweenError', {
            min: FORM_FIELDS_CONFIG[2].min,
            max: FORM_FIELDS_CONFIG[2].max,
          }),
        }),
    })
    .unknown(true);

  const proposedSalarySchema = Joi.object({
    unit: Joi.string().optional(),

    minValue: Joi.when(Joi.ref('...salaryDisplayOption'), {
      is: Joi.valid(...salaryOptions),
      then: Joi.string()
        .pattern(/^\d+(\.\d{1,2})?$/)
        .required()
        .custom((value: string, helpers: Joi.CustomHelpers): Joi.ErrorReport | string => {
          if (parseFloat(value) <= 0) {
            return helpers.error('num.greaterThanZero');
          }
          return value;
        })
        .messages({
          'string.empty': t('createJobPosition:salaryDisplayOptions.minSalaryRequired'),
          'string.pattern.base': t('createJobPosition:salaryDisplayOptions.invalidNumber'),
          'any.only': t('createJobPosition:salaryDisplayOptions.invalidOption'),
          'num.greaterThanZero': t('createJobPosition:salaryDisplayOptions.numGreaterThanZero'),
        }),
    }),

    maxValue: Joi.when('...salaryDisplayOption', {
      switch: [
        {
          is: Joi.valid(...rangeRequiredDisplayOptions),
          then: Joi.string()
            .pattern(/^\d+(\.\d{1,2})?$/)
            .required()
            .custom(validateMaxGreaterThanMin)
            .messages({
              'string.empty': t('createJobPosition:salaryDisplayOptions.maxSalaryRequired'),
              'string.pattern.base': t('createJobPosition:salaryDisplayOptions.invalidNumber'),
              'number.maxLessThanMin': t('createJobPosition:salaryDisplayOptions.maxGreaterThanMin'),
            }),
        },
        {
          is: Joi.valid(SalaryDisplayOption.STARTING_AT, SalaryDisplayOption.FIXED, SalaryDisplayOption.HIDDEN),
          then: Joi.string()
            .pattern(/^\d+(\.\d{1,2})?$/)
            .allow('')
            .allow(null)
            .optional()
            .custom((value: string, helpers: Joi.CustomHelpers): Joi.ErrorReport | string => {
              if (!value) return value;
              return validateMaxGreaterThanMin(value, helpers);
            })
            .messages({
              'string.pattern.base': t('createJobPosition:salaryDisplayOptions.invalidNumber'),
              'number.maxLessThanMin': t('createJobPosition:salaryDisplayOptions.maxGreaterThanMin'),
            }),
        },
      ],
      otherwise: Joi.string().allow('').allow(null).optional(),
    }),

    customUnit:
      jobPositionData.proposedSalary.unit === 'custom'
        ? Joi.string()
            .required()
            .messages({
              'string.empty': t('createJobPosition:unitRequired'),
              'string.base': t('createJobPosition:unitRequired'),
              'any.required': t('createJobPosition:unitRequired'),
            })
        : Joi.string().allow('').allow(null).optional(),
  });

  const smbProposedSalarySchema = Joi.object().keys({
    unit: Joi.string()
      .required()
      .messages({
        'string.empty': t('createJobPosition:salaryUnitRequired'),
      }),
    minValue: Joi.number()
      .required()
      .greater(0)
      .messages({
        'any.required': t('createJobPosition:minValueRequired'),
      }),
    maxValue: Joi.number()
      .required()
      .greater(Joi.ref('minValue'))
      .messages({
        'any.required': t('createJobPosition:maxValueRequired'),
      }),
    customUnit:
      jobPositionData.proposedSalary.unit === 'custom'
        ? Joi.string()
            .required()
            .messages({
              'string.empty': t('createJobPosition:unitRequired'),
              'string.base': t('createJobPosition:unitRequired'),
              'any.required': t('createJobPosition:unitRequired'),
            })
        : Joi.string().allow('').allow(null).optional(),
  });

  const smbJobDetailSchema = Joi.object()
    .options({ abortEarly: false })
    .keys({
      positionId: Joi.string()
        .required()
        .messages({
          'string.empty': t('createJobPosition:positionIdRequired'),
        }),
      industry: Joi.string()
        .required()
        .messages({
          'string.empty': t('createJobPosition:industryRequired'),
        }),
      occupation: Joi.string()
        .required()
        .messages({
          'string.empty': t('createJobPosition:occupationRequired'),
        }),
      job: Joi.string()
        .required()
        .messages({
          'string.empty': t('createJobPosition:jobRequired'),
        }),
      jobCustomName: Joi.string()
        .trim(true)
        .required()
        .messages({
          'string.empty': t('createJobPosition:jobTitleRequired'),
        }),
      noOfOpenings: Joi.number()
        .required()
        .messages({
          'number.base': t('createJobPosition:noOfOpeningsRequired'),
        }),
      jobDescription: Joi.string()
        .trim(true)
        .required()
        .messages({
          'string.empty': t('createJobPosition:jobDescriptionRequired'),
        }),
      proposedSalary: smbProposedSalarySchema,
      salaryDisplayOption: Joi.string()
        .valid(...salaryOptions)
        .required()
        .messages({
          'string.empty': t('createJobPosition:salaryDisplayOptions.required'),
          'any.required': t('createJobPosition:salaryDisplayOptions.required'),
          'string.base': t('createJobPosition:salaryDisplayOptions.required'),
          'any.only': t('createJobPosition:salaryDisplayOptions.invalid'),
        }),
    })
    .unknown(true);

  const screeningOptionsSchema = Joi.object()
    .keys({
      request_instant_verify_education: Joi.boolean(),
      request_instant_verify_employment: Joi.boolean(),
      request_enhanced_identity_verification: Joi.boolean(),
      request_criminal_record_check: Joi.boolean(),
      request_enhanced_criminal_record_check: Joi.boolean(),
      request_us_criminal_record_check_tier_1: Joi.boolean(),
      request_us_criminal_record_check_tier_2: Joi.boolean(),
      request_soquij: Joi.boolean(),
      request_right_to_work: Joi.boolean(),
    })
    .custom((value, helpers) => {
      // Ensure screeningOptions is a valid object
      // eslint-disable-next-line
      if (!isCertnEnabled) return value;
      // eslint-disable-next-line
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return helpers.error('any.invalid', {
          message: '',
        });
      }

      // Ensure object has at least one valid key that is `true`
      if (Object.keys(value).length === 0 || !Object.values(value).some((v) => v === true)) {
        return helpers.error('any.invalid', {
          message: '',
        });
      }
      // eslint-disable-next-line
      return value;
    }, 'Screening options validation');

  const jobDetailSchema = (positionEdit?: JopPositionResponse | null) => {
    const isEditing = !!positionEdit;
    const hasTemplates =
      jobPositionData && jobPositionData?.languageEvaluationTemplates && jobPositionData?.languageEvaluationTemplates?.length > 0;

    // const hasTemplateId = !!positionDataToEdit?.leTemplateId;

    return Joi.object()
      .options({ abortEarly: false })
      .keys({
        positionId: Joi.string()
          .required()
          .messages({
            'string.empty': t('createJobPosition:positionIdRequired'),
          }),
        industry: Joi.string()
          .required()
          .messages({
            'string.empty': t('createJobPosition:industryRequired'),
          }),
        occupation: Joi.string()
          .required()
          .messages({
            'string.empty': t('createJobPosition:occupationRequired'),
          }),
        job: Joi.string()
          .required()
          .messages({
            'string.empty': t('createJobPosition:jobRequired'),
          }),
        jobCustomName: Joi.string()
          .trim(true)
          .required()
          .messages({
            'string.empty': t('createJobPosition:jobTitleRequired'),
          }),
        positionType: isCountryAustralia
          ? Joi.string()
              .required()
              .messages({
                'string.empty': t('createJobPosition:positionTypeRequired'),
              })
          : Joi.string().allow('').optional(),
        noOfOpenings: isRequisitionEnabled
          ? Joi.number().allow(null).optional()
          : Joi.number()
              .required()
              .messages({
                'number.base': t('createJobPosition:noOfOpeningsRequired'),
              }),
        experienceFilterType: Joi.string().allow('').allow(null).optional(),
        requiredExperience: Joi.number().allow('').allow(null).optional(),
        jobDescription: Joi.string().allow('').optional(),
        modernHirePassThreshold: Joi.number().when('modernHireEnable', {
          is: Joi.boolean().valid(true),
          then: Joi.number()
            .min(1)
            .positive()
            .required()
            .messages({
              'string.empty': t('createJobPosition:modernHireThresholdValueNotEmptyError'),
              'any.required': t('createJobPosition:modernHireThresholdValueNotEmptyError'),
              'number.min': t('createJobPosition:modernHireThresholdValueNotZeorError'),
            }),
          otherwise: Joi.allow('').allow(null),
        }),

        languageEvaluationPassThreshold: Joi.number().when('languageEvaluationEnable', {
          is: Joi.boolean().valid(true),
          then: Joi.number()
            .min(1)
            .positive()
            .required()
            .messages({
              'string.empty': t('createJobPosition:languageThresholdValueEmptyError'),
              'any.required': t('createJobPosition:languageThresholdValueEmptyError'),
              'number.min': t('createJobPosition:languageThresholdValueEmptyError'),
            }),
          otherwise: Joi.allow('').allow(null),
        }),

        // languageEvaluationTemplates: Joi.array().when('languageEvaluationEnable', {
        //   is: Joi.boolean().valid(true),
        //   then:
        //     // eslint-disable-next-line no-nested-ternary
        //     isTemplateUsed
        //       ? Joi.allow(null)
        //       : // eslint-disable-next-line no-nested-ternary
        //       isEditing
        //       ? _isNil(positionDataToEdit?.leTemplateId) && hasTemplates
        //         ? Joi.array()
        //             .min(1)
        //             .messages({
        //               'array.min': t('createJobPosition:templateRequiredError'),
        //             })
        //         : Joi.allow(null) // ✅ allow when leTemplateId is present or hasTemplates is false
        //       : !isLETemplateEnabled
        //       ? Joi.array()
        //           .min(1)
        //           .messages({
        //             'array.min': t('createJobPosition:templateRequiredError'),
        //           })
        //       : Joi.allow(null),
        //   otherwise: Joi.allow(null),
        // }),

        languageEvaluationTemplates: Joi.array().when('languageEvaluationEnable', {
          is: Joi.boolean().valid(true),
          then: isLETemplateEnabled
            ? isTemplateUsed // Existing template used
              ? Joi.allow(null)
              : isEditing // Editing an existing position
              ? _isNil(positionDataToEdit?.leTemplateId) && hasTemplates
                ? Joi.array()
                    .min(1)
                    .messages({
                      'array.min': t('createJobPosition:templateRequiredError'),
                    })
                : Joi.allow(null) // If editing and leTemplateId is present, or no templates
              : Joi.allow(null) // Allow null when creating, regardless of template status
            : Joi.array()
                .min(1)
                .messages({
                  'array.min': t('createJobPosition:templateRequiredError'),
                }),
          otherwise: Joi.allow(null),
        }),

        dialogFlowAgents: Joi.array().when('dialogFlowEnabled', {
          is: Joi.boolean().valid(true),
          then: Joi.array()
            .min(1)
            .required()
            .messages({
              'array.min': t('createJobPosition:languageAgentRequiredError'),
              'any.required': t('createJobPosition:languageAgentRequiredError'),
            }),
          otherwise: Joi.allow('').allow(null),
        }),

        modernHireTemplateId: Joi.string().when('modernHireEnable', {
          is: Joi.boolean().valid(true),
          then: Joi.string()
            .required()
            .messages({
              'string.empty': t('createJobPosition:modernHireTemplateIdError'),
              'any.required': t('createJobPosition:modernHireTemplateIdError'),
            }),
          otherwise: Joi.allow('').allow(null),
        }),
        // leTemplateId: Joi.any().when('languageEvaluationEnable', {
        //   is: Joi.boolean().valid(true),
        //   then: Joi.any()
        //     .optional() // ensure it runs custom logic even if undefined
        //     .custom((value, helpers) => {
        //       const isEmptyValue = _isNil(value) || value === '';
        //       const templates = jobPositionData?.languageEvaluationTemplates || [];
        //       const hasOldTemplates = Array.isArray(templates) && templates.length > 0;

        //       if (!isEditing && isEmptyValue) {
        //         return helpers.error('any.required');
        //       }

        //       if (isEditing) {
        //         if (isTemplateUsed && isEmptyValue) {
        //           return helpers.error('any.required');
        //         }

        //         if (!isTemplateUsed && isEmptyValue && !hasOldTemplates) {
        //           return helpers.error('any.required');
        //         }

        //       // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        //       return value; // all valid cases
        //     })
        //     .messages({
        //       'any.required': t('createJobPosition:languageEvaluationTemplateError'),
        //     }),
        //   otherwise: Joi.allow('').allow(null),
        // }),
        leTemplateId: Joi.string().when('languageEvaluationEnable', {
          is: Joi.boolean().valid(true),
          // eslint-disable-next-line no-nested-ternary
          then: isTemplateUsed
            ? Joi.string()
                .required()
                .messages({
                  'string.empty': t('createJobPosition:languageEvaluationTemplateError'),
                  'any.required': t('createJobPosition:languageEvaluationTemplateError'),
                })
            : isEditing && hasTemplates
            ? Joi.allow(null)
            : !isLETemplateEnabled
            ? Joi.allow('').allow(null)
            : Joi.string()
                .required()
                .messages({
                  'string.empty': t('createJobPosition:languageEvaluationTemplateError'),
                  'any.required': t('createJobPosition:languageEvaluationTemplateError'),
                }),
          otherwise: Joi.allow('').allow(null),
        }),
        enableScheduleMatch: Joi.boolean()
          .optional()
          .custom((enabled: boolean | undefined, helpers) => {
            // When schedule matching is enabled, the Fitband Template feature must be enabled in the account.
            if (enabled && !isFitbandTemplateEnabled) {
              return helpers.error('any.custom');
            }
            return enabled;
          }, 'Schedule Match and Fitband Template Validation')
          .messages({
            'any.custom': t('createJobPosition:scheduleMatchFitbandError'),
          }),
        fitBandTemplateId: Joi.string().when('fitBandTemplateEnable', {
          is: Joi.boolean().valid(true),
          then: Joi.string()
            .required()
            .messages({
              'string.empty': t('createJobPosition:fitband.fitbandTemplateError'),
              'any.required': t('createJobPosition:fitband.fitbandTemplateError'),
            }),
          otherwise: Joi.allow('').allow(null),
        }),
        salaryDisplayOption: Joi.string()
          .valid(...salaryOptions)
          .required()
          .messages({
            'string.empty': t('createJobPosition:salaryDisplayOptions.required'),
            'any.required': t('createJobPosition:salaryDisplayOptions.required'),
            'string.base': t('createJobPosition:salaryDisplayOptions.required'),
            'any.only': t('createJobPosition:salaryDisplayOptions.invalid'),
          }),
        proposedSalary: proposedSalarySchema,
        interview: interviewSchema,
        automationConfiguration: automationConfigurationSchema,
        enableScreeningOptions: Joi.boolean().required(),
        screeningOptions: Joi.alternatives()
          .optional()
          .conditional('enableScreeningOptions', {
            is: true,
            then: screeningOptionsSchema.required().messages({
              'any.required': t('createJobPosition:certnIntegrationError'),
              'any.invalid': t('createJobPosition:certnIntegrationError'),
            }),
            otherwise: Joi.allow(null),
          }),
        automateHiringAfterOffer: Joi.boolean().optional(),
        shouldOverlap: Joi.boolean().optional(),
        maxLimitInterviews: Joi.number()
          .allow(null)
          .min(FORM_FIELDS_CONFIG[3].min)
          .max(FORM_FIELDS_CONFIG[3].max)
          .optional()
          .label(FORM_FIELDS_CONFIG[3].key)
          .messages({
            'number.min': t('createJobPosition:mustBeBetweenError', {
              min: FORM_FIELDS_CONFIG[3].min,
              max: FORM_FIELDS_CONFIG[3].max,
            }),
            'number.max': t('createJobPosition:mustBeBetweenError', {
              min: FORM_FIELDS_CONFIG[3].min,
              max: FORM_FIELDS_CONFIG[3].max,
            }),
            'number.unsafe': t('createJobPosition:mustBeBetweenError', {
              min: FORM_FIELDS_CONFIG[3].min,
              max: FORM_FIELDS_CONFIG[3].max,
            }),
          }),
        requisitionActiveThresholdDays: Joi.number()
          .allow(null)
          .min(FORM_FIELDS_CONFIG[5].min)
          .max(FORM_FIELDS_CONFIG[5].max)
          .optional()
          .label(FORM_FIELDS_CONFIG[5].key)
          .messages({
            'number.min': t('createJobPosition:mustBeBetweenError', {
              min: FORM_FIELDS_CONFIG[5].min,
              max: FORM_FIELDS_CONFIG[5].max,
            }),
            'number.max': t('createJobPosition:mustBeBetweenError', {
              min: FORM_FIELDS_CONFIG[5].min,
              max: FORM_FIELDS_CONFIG[5].max,
            }),
            'number.unsafe': t('createJobPosition:mustBeBetweenError', {
              min: FORM_FIELDS_CONFIG[5].min,
              max: FORM_FIELDS_CONFIG[5].max,
            }),
          }),
      })
      .unknown(true);
  };

  const formValidation = (formData: unknown, scheme: Joi.ObjectSchema) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { error } = scheme.validate(formData);
    if (error) {
      setFormErrors(errorFormat(error.details));
      return false;
    }
    setFormErrors([]);
    return true;
  };

  const handleChange = (value: string | Date | number | boolean | null, fieldName: string, dataType = false) => {
    updateObject(jobPositionData, fieldName, value, dataType);

    if (fieldName === 'industry') {
      jobPositionData.occupation = '';
      jobPositionData.job = '';
      jobPositionData.industryName = getIndustryName(value);
    }

    if (fieldName === 'occupation') {
      jobPositionData.job = '';
    }

    if (fieldName === 'modernHireEnable') {
      setFormErrors([]);
    } else {
      setFormErrors(clearErrorMessage(formErrors, fieldName));
    }

    if (fieldName === 'proposedSalary.unit') {
      if (value !== 'custom') {
        jobPositionData.proposedSalary.customUnit = null;
      }
    }

    if (fieldName === 'languageEvaluationEnable') {
      jobPositionData.languageEvaluationEnable = (value as boolean) ?? undefined;
      jobPositionData.languageEvaluationTemplates = value ? jobPositionData.languageEvaluationTemplates ?? [] : undefined;
      jobPositionData.languageEvaluationPassThreshold = value
        ? jobPositionData.languageEvaluationPassThreshold ?? DEFAULT_LANGUAGE_EVALUATION_PASS_THRESHOLD
        : undefined;
    }

    setJobPositionData({ ...jobPositionData });
    setFormErrors([]);
  };

  const handleSalaryChange = (value: string, fieldName: string) => {
    setFormErrors(clearErrorMessage(formErrors, `proposedSalary.${fieldName}`));
    const translated = !/^(?:\d*[.|,]\d{1,2}|\d+)$/.exec(value);
    let allowInput = true;

    const spliterator = value.includes('.') ? value.split('.') : value.split(',');
    if (spliterator[1]) {
      if (spliterator[1].length > 2) {
        allowInput = false;
      }
    }
    if (allowInput) {
      setJobPositionData((prevData) => ({
        ...prevData,
        proposedSalary: {
          ...prevData.proposedSalary,
          [fieldName]: value.toString().replace(',', '.'),
        },
      }));

      if (value === '' || !translated) {
        setSalaryError([]);
      } else {
        const hasAlreadyError = salaryError.filter((err) => err.label === fieldName);
        if (hasAlreadyError.length < 1) {
          setSalaryError((prevData) => [
            ...prevData,
            {
              label: fieldName,
              message: t('createJobPosition:invalidNumberFormat'),
            },
          ]);
        }
      }

      if (fieldName === 'maxValue') {
        if (
          jobPositionData.proposedSalary.minValue !== '' &&
          Number(jobPositionData.proposedSalary.minValue) > parseFloat(value)
        ) {
          setSalaryError((prevData) => [
            ...prevData,
            {
              label: fieldName,
              message: t('createJobPosition:maxValueShouldBeGreaterThanMinValue', {
                minValue: jobPositionData.proposedSalary.minValue,
              }),
            },
          ]);
        }
        if (jobPositionData.proposedSalary.minValue === '') {
          setSalaryError((prevData) => [
            ...prevData,
            {
              label: 'minValue',
              message: t('createJobPosition:minValueRequired'),
            },
          ]);
        }

        if (!/^(?:\d*[.|,]\d{1,2}|\d+)$/.exec(jobPositionData.proposedSalary.minValue.toString())) {
          setSalaryError((prevData) => [
            ...prevData,
            {
              label: 'minValue',
              message: t('createJobPosition:invalidNumberFormat'),
            },
          ]);
        }
        if (!_isNil(jobPositionData.proposedSalary.minValue) && value === '') {
          setSalaryError((prevData) => [
            ...prevData,
            {
              label: fieldName,
              message: t('createJobPosition:maxValueRequired'),
            },
          ]);
        }
        if (value === '') {
          setSalaryError([]);
        }
        if (jobPositionData.proposedSalary.minValue === '' && value === '') {
          setSalaryError([]);
        }
      }

      if (fieldName === 'minValue') {
        if (
          jobPositionData.proposedSalary.maxValue !== '' &&
          Number(jobPositionData.proposedSalary.maxValue) < parseFloat(value)
        ) {
          setSalaryError((prevData) => [
            ...prevData,
            {
              label: fieldName,
              message: t('createJobPosition:minValueShouldBeLowerThanMaxValue', {
                maxValue: jobPositionData.proposedSalary.maxValue,
              }),
            },
          ]);
        }
        if (jobPositionData.proposedSalary.maxValue !== '') {
          if (!/^(?:\d*[.|,]\d{1,2}|\d+)$/.exec(jobPositionData.proposedSalary.maxValue.toString())) {
            setSalaryError((prevData) => [
              ...prevData,
              {
                label: 'maxValue',
                message: t('createJobPosition:invalidNumberFormat'),
              },
            ]);
          }
        }

        if (!_isNil(jobPositionData.proposedSalary.maxValue) && value === '') {
          setSalaryError((prevData) => [
            ...prevData,
            {
              label: fieldName,
              message: t('createJobPosition:minValueRequired'),
            },
          ]);
        }
        if (jobPositionData.proposedSalary.maxValue === '' && value === '') {
          setSalaryError([]);
        }
      }
    }
  };

  const handleAutomationChange = (value: string | null, fieldName: string, dataType = false) => {
    updateObject(jobPositionData, fieldName, value, dataType);
    if (value === AutomationTypes.SCREENING) {
      jobPositionData.automationType = AutomationTypes.SCREENING;
      jobPositionData.automated = true;
      jobPositionData.automationConfiguration = { ...jobPositionData.automationConfiguration, target: null };
    } else if (value === AutomationTypes.SCHEDULING) {
      jobPositionData.automationType = AutomationTypes.SCHEDULING;
      jobPositionData.automated = true;
      delete jobPositionData.automationConfiguration;
    } else {
      jobPositionData.automationType = null;
      jobPositionData.automated = false;
      delete jobPositionData.automationConfiguration;
    }
    setFormErrors(clearErrorMessage(formErrors, fieldName));
    setJobPositionData({ ...jobPositionData });
  };

  const handleJobChange = (value: string, fieldName: string) => {
    let skillList: SkillForPosition[] = [];
    /* istanbul ignore else */
    if (!_isEmpty(value)) {
      // eslint-disable-next-line radix
      const filterSkills = jobList.filter((job) => job.id === parseInt(value));
      skillList = filterSkills[0].skills.map((skill) => ({
        id: skill.id,
        value: 0.5,
      }));
    }

    setJobPositionData((prevData) => ({
      ...prevData,
      [fieldName]: value,
      skills: skillList,
    }));
    setFormErrors(clearErrorMessage(formErrors, fieldName));
  };

  const isOverviewVideoFeatureEnabled: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_OVERVIEW_VIDEO];
  });

  const handleAvailabilityChange = (availability: { [key: string]: { start_1: string; end_1: string } }) => {
    setJobPositionData((prevData) => ({
      ...prevData,
      newAvailabilities: availability,
    }));
  };

  const handleSkillChange = (value: number, fieldName: number) => {
    Object.assign(jobPositionData?.skills?.find((skill) => skill.id === fieldName) || {}, { value });

    setJobPositionData((prevData) => ({
      ...prevData,
      skills: jobPositionData.skills,
    }));
  };

  const handleLanguageEvaluationPhrase = (checked: boolean, language: string, phrase: LanguageEvaluationPhrases) => {
    const formatPhrase = {
      language,
      phrase: phrase.description || '',
      phraseId: phrase.id || '',
    };

    if (checked) {
      jobPositionData.languageEvaluationTemplates?.push(formatPhrase);
      if (jobPositionData.languageEvaluationTemplates?.length) {
        setFormErrors(clearErrorMessage(formErrors, 'languageEvaluationTemplates'));
      }
    } else {
      const indexOfItem = _findIndex(jobPositionData.languageEvaluationTemplates, { phraseId: phrase.id });
      if (indexOfItem > -1) {
        jobPositionData.languageEvaluationTemplates?.splice(indexOfItem, 1);
      }
    }

    setJobPositionData((prevData) => ({
      ...prevData,
      languageEvaluationTemplates: jobPositionData.languageEvaluationTemplates,
    }));
  };

  const handleDialogFlowAgents = (agent: DialogFlowAgent | undefined) => {
    if (agent) {
      const { location, defaultLanguageCode, agentId, displayName } = agent;
      const formatAgent = {
        location,
        defaultLanguageCode,
        agentId,
        displayName,
      };

      setJobPositionData((prevData) => ({
        ...prevData,
        dialogFlowAgents: [formatAgent],
      }));

      setFormErrors(clearErrorMessage(formErrors, 'dialogFlowAgents'));
    }
  };

  const handleDialogFlowCheckbox = (checked: boolean) => {
    setJobPositionData((prevData) => ({
      ...prevData,
      dialogFlowEnabled: checked,
      dialogFlowAgents: checked ? [] : undefined,
    }));
  };

  const handleLanguageEvaluation = (checked: boolean) => {
    setJobPositionData((prevData) => ({
      ...prevData,
      languageEvaluationEnable: checked, // Synchronize enable field
      languageEvaluationTemplates: checked ? [] : undefined,
      languageEvaluationPassThreshold: checked
        ? prevData.languageEvaluationPassThreshold ?? DEFAULT_LANGUAGE_EVALUATION_PASS_THRESHOLD
        : undefined,
    }));
  };

  const handleLanguageChange = () => {
    setJobPositionData((prevData) => ({
      ...prevData,
      languageEvaluationTemplates: [],
    }));
  };

  const handleChangeInterviewLocation = (isSameLocation: boolean) => {
    setJobPositionData((prevData) => ({
      ...prevData,
      interview: {
        ...prevData.interview,
        interviewLocation: isSameLocation ? jobPositionData.businessAddress : '',
      },
    }));
    setIsInterviewAndBusinessLocationSame(isSameLocation);
    setFormErrors(clearErrorMessage(formErrors, 'interview.interviewLocation'));
  };

  const deleteQuestion = (questionId: number | undefined) => {
    setJobPositionData((prevData) => ({
      ...prevData,
      specificQuestions: jobPositionData.specificQuestions.filter((sQue, index) => index !== questionId),
    }));
  };

  // eslint-disable-next-line consistent-return
  const addQuestion = (question: Question, questionEditMode: number | undefined) => {
    if (_isNil(questionEditMode)) {
      setJobPositionData((prevData) => ({
        ...prevData,
        specificQuestions: [...prevData.specificQuestions, { ...question }],
      }));
    } else {
      if (jobPositionData.specificQuestions.length > 0) {
        Object.assign(jobPositionData?.specificQuestions.find((ques, index) => index === questionEditMode) || {}, {
          ...question,
        });
      } else {
        jobPositionData.specificQuestions.push(question);
      }
      setJobPositionData((prevData) => ({
        ...prevData,
        specificQuestions: jobPositionData.specificQuestions,
      }));
    }
  };

  const deleteFreeFormQuestion = (questionId: number | undefined) => {
    setJobPositionData((prevData) => ({
      ...prevData,
      freeFormQuestions: jobPositionData.freeFormQuestions.filter((sQue, index) => index !== questionId),
    }));
  };

  const deleteMultipleQuestionsQuestion = (questionId: number | null) => {
    setJobPositionData((prevData) => ({
      ...prevData,
      multipleChoiceQuestions: jobPositionData.multipleChoiceQuestions.filter((sQue, index) => index !== questionId),
    }));
  };

  // eslint-disable-next-line consistent-return
  const addFreeFormQuestion = (freeFormQuestion: FreeFormQuestion, freeFormQuestionEditMode: number | undefined) => {
    if (_isNil(freeFormQuestionEditMode)) {
      setJobPositionData((prevData) => ({
        ...prevData,
        freeFormQuestions: [...prevData.freeFormQuestions, { ...freeFormQuestion }],
      }));
    } else {
      if (jobPositionData.freeFormQuestions.length > 0) {
        Object.assign(jobPositionData?.freeFormQuestions?.find((ques, index) => index === freeFormQuestionEditMode) || {}, {
          ...freeFormQuestion,
        });
      } else {
        jobPositionData.freeFormQuestions.push(freeFormQuestion);
      }
      setJobPositionData((prevData) => ({
        ...prevData,
        freeFormQuestions: jobPositionData.freeFormQuestions,
      }));
    }
  };
  const addMultipleChoiceQuestion = (
    multipleChoiceQuestions: MultipleChoiceQuestion,
    multipleQuestionEditMode: number | undefined,
  ) => {
    if (_isNil(multipleQuestionEditMode)) {
      setJobPositionData((prevData) => ({
        ...prevData,
        multipleChoiceQuestions: [...prevData.multipleChoiceQuestions, { ...multipleChoiceQuestions }],
      }));
    } else {
      if (jobPositionData.multipleChoiceQuestions.length > 0) {
        Object.assign(jobPositionData?.multipleChoiceQuestions?.find((ques, index) => index === multipleQuestionEditMode) || {}, {
          ...multipleChoiceQuestions,
        });
      } else {
        jobPositionData.multipleChoiceQuestions.push(multipleChoiceQuestions);
      }
      setJobPositionData((prevData) => ({
        ...prevData,
        multipleChoiceQuestions: jobPositionData.multipleChoiceQuestions,
      }));
    }
  };

  const handleOverviewVideoUpload = async ({
    userId,
    positionId,
    overviewVideo,
    overviewThumbnail,
  }: {
    userId: string;
    positionId: string;
    overviewVideo: File;
    overviewThumbnail: File;
  }) => {
    await uploadOverviewVideo({ userId, positionId, overviewVideo });
    await uploadOverviewThumbnail({ userId, positionId, overviewThumbnail });
    await uploadOverviewVideoConfirmCall({ positionId });
  };

  const saveJobPosition = async () => {
    onCloseReview();

    if (!jobPositionData.positionType) {
      jobPositionData.positionType = undefined;
    }

    if (user) {
      setIsLoading(true);
      const cleanedConditions = sanitizeOfferConditions(jobPositionData?.offerConditions as LanguageMap<string>);

      if (_isNil(cleanedConditions)) {
        jobPositionData.offerConditions = null;
      } else {
        jobPositionData.offerConditions = cleanedConditions;
      }

      if (
        _isNil(accounts?.configuration?.certnEnabled) ||
        // !accounts?.configuration?.certnEnabled ||
        _isNil(jobPositionData.screeningOptions) ||
        !jobPositionData.enableScreeningOptions
      ) {
        jobPositionData.screeningOptions = null;
      }

      if (!enableToggleOfBackgroundCheckOnPositionLevel) {
        jobPositionData.enableScreeningOptions = false;
      }

      // if (_isNil(jobPositionData.languageEvaluationTemplates) || !jobPositionData.languageEvaluationTemplates.length) {
      //   jobPositionData.languageEvaluationPassThreshold = undefined;
      // }

      if (!jobPositionData.languageEvaluationEnable) {
        jobPositionData.leTemplateId = null;
        jobPositionData.languageEvaluationPassThreshold = undefined;
      }

      if (!jobPositionData.fitBandTemplateEnable) {
        jobPositionData.fitBandTemplateId = undefined;
      }

      if (_isEmpty(jobPositionData.videoUrlId)) {
        jobPositionData.videoUrlId = null;
      }

      if (isUndefined(isLETemplateEnabled) || !isLETemplateEnabled) {
        jobPositionData.leTemplateId = null;
      }

      if (
        jobPositionData?.screeningOptions &&
        !Object.keys(Object.fromEntries(Object.entries(jobPositionData?.screeningOptions).filter(([, v]) => v))).length
      ) {
        jobPositionData.screeningOptions = _isNil(positionDataToEdit) ? undefined : null;
      }

      if (!isQuestionTemplateEnabled) {
        jobPositionData.questionTemplateId = null;
      }

      if (_isNil(positionDataToEdit)) {
        if (selectedBusiness) {
          jobPositionData.business = selectedBusiness.id;
          jobPositionData.businessAddress = selectedBusiness.completeAddress;
          jobPositionData.businessName = selectedBusiness.name;
        }
        if (isSmbRecruiter && businessIdFromParams) {
          jobPositionData.business = businessIdFromParams;
        }
        if (!accounts?.subscription?.automation) {
          jobPositionData.automationType = null;
          jobPositionData.automated = false;
        }

        if (!jobPositionData?.languageEvaluationEnable) {
          jobPositionData.leTemplateId = undefined;
        }

        const newPostition = await createJobPosition({
          accountId: user.account,
          jobPositionData,
        })
          .then((res) => res)
          .catch((error: unknown) => {
            // Handle 409 conflict (duplicate positionId)
            const axiosErr = error as { response?: { status?: number; data?: { message?: string } } };
            if (axiosErr?.response?.status === 409) {
              setFormErrors((err) => [
                ...err,
                {
                  label: 'positionId',
                  message: axiosErr.response?.data?.message ?? t('createJobPosition:positionIdExistsError'),
                },
              ]);
              throw error;
            }

            const hireFormError = handleHireFormErrors({
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              error,
            });
            if (hireFormError.length > 0) {
              setFormErrors(hireFormError);
              throw error;
            } else {
              let defaultMessage = t('createJobPosition:errorPositionCreation');
              const newError = error as AxiosError<{ message: string }>;
              if (axios.isAxiosError(newError)) {
                defaultMessage = newError.response?.data?.message || defaultMessage;
              }
              toast({
                description: defaultMessage,
                status: 'error',
                duration: 6000,
                isClosable: true,
              });
              throw error;
            }
          })
          .finally(() => {
            if (!(overviewVideoFile && overviewThumbnailFile && isOverviewVideoFeatureEnabled)) setIsLoading(false);
          });
        jobPositionData.positionId = newPostition.positionId;
        if (overviewVideoFile && overviewThumbnailFile && isOverviewVideoFeatureEnabled) {
          await handleOverviewVideoUpload({
            userId: user.id,
            positionId: newPostition.positionId,
            overviewVideo: overviewVideoFile,
            overviewThumbnail: overviewThumbnailFile,
          });
        }
        setIsLoading(false);
        setJobPositionData(initialJobPositionData);
        toast({
          title: t('createJobPosition:jobCreatedSuccessMessage'),
          status: 'info',
          duration: 6000,
          isClosable: true,
        });
        const currentBusinessLocation = businessData.find((business) => business.id === jobPositionData.business);
        if (currentBusinessLocation) {
          const newpositionQueryParams = positionQueryParams;
          newpositionQueryParams.positionStatus = 'closed';
          setPositionQueryParams({ ...newpositionQueryParams });
        }
      } else {
        let updateSucceeded = false;

        let apiCall;
        if (isCopyingPosition) {
          apiCall = copyJobPosition({
            jobPositionToCopy: {
              ...jobPositionData,
              sourcePositionId: positionDataToEdit?.id,
              customName: jobPositionData.jobCustomName,
              description: jobPositionData.jobDescription,

              multipleChoiceQuestions: map(jobPositionData.multipleChoiceQuestions, (q) => ({
                ..._omit(q, 'id'),
                options: map(q.options, (opt) => _omit(opt, 'id')),
              })),

              freeFormQuestions: map(jobPositionData.freeFormQuestions, (q) => _omit(q, 'id')),

              specificQuestions: map(jobPositionData.specificQuestions, (q) => _omit(q, 'id')),
            },
          });
        } else {
          apiCall = updateJobPosition({
            accountId: user.account,
            jobPositionData,
            id: positionDataToEdit?.id,
          });
        }

        await apiCall
          .then(async () => {
            if (overviewVideoFile && overviewThumbnailFile && isOverviewVideoFeatureEnabled) {
              await handleOverviewVideoUpload({
                userId: user.id,
                positionId: positionDataToEdit.id,
                overviewVideo: overviewVideoFile,
                overviewThumbnail: overviewThumbnailFile,
              });
            }
            updateSucceeded = true;
          })
          .catch((error) => {
            const axiosErr = error as { response?: { status?: number; data?: { message?: string } } };
            if (axiosErr?.response?.status === 409) {
              setFormErrors((err) => [
                ...err,
                {
                  label: 'positionId',
                  message: axiosErr.response?.data?.message ?? t('createJobPosition:positionIdExistsError'),
                },
              ]);
            } else {
              const newError = handleHireFormErrors({ error });
              if (newError.length > 0) {
                setFormErrors(newError);
              }
            }
            throw error;
          })
          .finally(() => {
            setIsLoading(false);
          });

        if (updateSucceeded) {
          setTimeout(() => {
            setJobPositionData(initialJobPositionData);
          }, 2000);

          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          isCopyingPosition
            ? toast({
                title: t('createJobPosition:jobCopySuccessMessage'),
                status: 'info',
                duration: 6000,
                isClosable: true,
              })
            : toast({
                title: t('createJobPosition:jobUpdatedSuccessMessage'),
                status: 'info',
                duration: 6000,
                isClosable: true,
              });
        }
      }
      if (isCopyingPosition && !_isNil(onCompleteCopy)) {
        let defaultBusinessData;
        if (selectedBusiness) {
          defaultBusinessData = {
            ...selectedBusiness,
            id: jobPositionData.business,
            name: jobPositionData.businessName,
          };
        } else {
          defaultBusinessData = {
            id: jobPositionData.business,
            name: jobPositionData.businessName,
            cityName: '',
            address: '',
            country: 'Canada',
            latitude: 0,
            longitude: 0,
            zip: '',
            state: '',
            phoneNumber: '',
            webSite: '',
            completeAddress: '',
            placeId: '',
            positions: 0,
          };
        }

        onCompleteCopy(defaultBusinessData);
      }
      redirectToPositionAfterCreated(jobPositionData, Boolean(isCopyingPosition));
    }
  };

  const checkIfSalaryExists = (givenJobPositionData: JobPosition) => {
    if (givenJobPositionData.proposedSalary.minValue === '') {
      setSalaryError((prevData) => [
        ...prevData,
        {
          label: 'minValue',
          message: t('createJobPosition:minValueRequired'),
        },
      ]);
    }
    if (givenJobPositionData.proposedSalary.maxValue === '') {
      setSalaryError((prevData) => [
        ...prevData,
        {
          label: 'maxValue',
          message: t('createJobPosition:maxValueRequired'),
        },
      ]);
    }
  };

  const onSmbPositionSave = () => {
    const validate = formValidation(jobPositionData, smbJobDetailSchema);
    checkIfSalaryExists(jobPositionData);

    if (validate) {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      checkIfComplianceModalNeeded(saveJobPosition);
    }
  };

  const getInterviewData = (interview: Interview) => {
    return {
      interviewType: interview.interviewType ? interview.interviewType : 'video',
      interviewDuration: interview.interviewDuration ? interview.interviewDuration : 30,
      interviewMessage: interview.interviewMessage ? interview.interviewMessage : '',
      interviewLocation: interview.interviewLocation ? interview.interviewLocation : '',
    } as Interview;
  };

  const loadSkillsOfJobType = (jobId: string): SkillForPosition[] => {
    const { skills } = jobList.filter((job) => job.id.toString() === jobId)[0];
    if (skills.length > 0) {
      return skills.map((skill) => ({
        id: skill.id,
        value: skill.value,
      }));
    }
    return [];
  };

  const getBusinessAddress = (businessId: string) => {
    const selectedBusinessLocation = businessLocations.find((businessLocation) => businessLocation.id === businessId);

    if (selectedBusinessLocation) {
      return selectedBusinessLocation.label;
    }
    return '';
  };

  useEffect(() => {
    const isFreeFormQuestionEnabled = accounts?.configuration?.freeFormQuestionEnabled === true;
    setFreeFormQuestion(isFreeFormQuestionEnabled);
  }, [accounts]);

  useEffect(() => {
    jobPositionRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    loadIndustriesJobsOccupation().catch(/* istanbul ignore next */ (err) => console.log(err));
  }, [loadIndustriesJobsOccupation]);

  useEffect(() => {
    if (!_isNil(positionDataToEdit) && jobList.length > 0) {
      const jobTypeId = positionDataToEdit?.job;
      const industryId = jobList.filter((jobItem) => jobItem.id.toString() === jobTypeId)[0].industry;
      const occupationId = jobList.filter((jobItem) => jobItem.id.toString() === jobTypeId)[0].occupation;
      const proposedSalary = positionDataToEdit?.proposedSalary
        ? {
            ...positionDataToEdit?.proposedSalary,
            minValue: positionDataToEdit?.proposedSalary.minValue
              ? (positionDataToEdit?.proposedSalary.minValue / 100).toString()
              : '',
            maxValue: positionDataToEdit?.proposedSalary.maxValue
              ? (positionDataToEdit?.proposedSalary.maxValue / 100).toString()
              : '',
            unit: positionDataToEdit?.proposedSalary.unit ? positionDataToEdit?.proposedSalary.unit : 'hourly',
            customUnit: positionDataToEdit?.proposedSalary.customUnit ? positionDataToEdit?.proposedSalary.customUnit : null,
          }
        : initialSalaryData;
      const multipleChoiceQuestions: MultipleChoiceQuestion[] = [];
      if (positionDataToEdit.multipleChoiceQuestions) {
        positionDataToEdit?.multipleChoiceQuestions.forEach((que) => {
          const newQues = que;
          const newOptions: {
            text: string;
            isPreferredAnswer?: boolean | undefined;
            priority: number;
            id?: string | undefined;
          }[] = [];
          que.options.forEach((opt: { text: string; isPreferredAnswer?: boolean; priority: number; id?: string }) => {
            // eslint-disable-next-line no-param-reassign
            opt.isPreferredAnswer = que.preferredAnswers?.includes(opt.id ? opt.id : '');
            newOptions.push(opt);
          });
          newQues.options = newOptions;
          multipleChoiceQuestions.push(_omit(newQues, 'preferredAnswers'));
        });
      }
      const enableHireFormSolution = hireFormEnabledPositions
        ? _includes(hireFormEnabledPositions, positionDataToEdit.id)
        : false;

      const cleanedOfferConditions = sanitizeOfferConditions(jobPositionData.offerConditions as LanguageMap<string>);

      const availableRequisitionActiveThresholdDays =
        positionDataToEdit?.requisitionActiveThresholdDays || accountRequisitionActiveThresholdDays;

      setJobPositionData((prevData) => ({
        ...prevData,
        industryName: getTranslatedValue(industries.filter((industry) => industry?.id === industryId)[0]?.name, i18n.language),
        industry: industryId.toString(),
        occupation: occupationId.toString(),
        job: positionDataToEdit?.job,
        jobCustomName: positionDataToEdit?.customName ? positionDataToEdit?.customName : positionDataToEdit?.jobCustomName,
        business: positionDataToEdit?.business,
        businessName: positionDataToEdit?.businessName,
        businessAddress: positionDataToEdit.business ? getBusinessAddress(positionDataToEdit?.business) : '',
        noOfOpenings: positionDataToEdit.noOfOpenings ? positionDataToEdit.noOfOpenings : null,
        salaryDisplayOption: positionDataToEdit?.salaryDisplayOption || SalaryDisplayOption.STARTING_AT,
        proposedSalary,
        experienceFilterType: positionDataToEdit.experienceFilterType ? positionDataToEdit.experienceFilterType : null,
        requiredExperience: positionDataToEdit.requiredExperience ? positionDataToEdit.requiredExperience : null,
        jobDescription: positionDataToEdit.description ? positionDataToEdit.description : '',
        fullTime: positionDataToEdit.fullTime ? positionDataToEdit?.fullTime : false,
        positionType: positionDataToEdit.positionType || '',
        interview: positionDataToEdit.interview ? getInterviewData(positionDataToEdit.interview) : initialInterviewData,
        specificQuestions: positionDataToEdit?.specificQuestions ? positionDataToEdit.specificQuestions : [],
        freeFormQuestions: positionDataToEdit?.freeFormQuestions ? positionDataToEdit.freeFormQuestions : [],
        multipleChoiceQuestions,
        schedule: positionDataToEdit?.schedule ? positionDataToEdit?.schedule : initialWorkSchedule,
        skills: positionDataToEdit?.skills ? positionDataToEdit?.skills : loadSkillsOfJobType(positionDataToEdit?.job),
        automationType: positionDataToEdit.automationType ? positionDataToEdit.automationType : null,
        automated: positionDataToEdit.automated ? positionDataToEdit.automated : false,
        // eslint-disable-next-line object-shorthand
        enableHireFormSolution: enableHireFormSolution,
        hireFormTemplateId:
          enableHireFormSolution && isHireFormTemplateEnabled ? positionDataToEdit?.hireFormTemplateId ?? null : null,
        modernHireEnable: positionDataToEdit?.modernHireEnable ? positionDataToEdit?.modernHireEnable : false,
        enableScheduleMatch: positionDataToEdit?.enableScheduleMatch ? positionDataToEdit?.enableScheduleMatch : false,
        fitBandTemplateEnable: positionDataToEdit?.fitBandTemplateEnable ? positionDataToEdit?.fitBandTemplateEnable : false,

        fitBandTemplateId:
          positionDataToEdit?.fitBandTemplateEnable && isFitbandTemplateEnabled
            ? positionDataToEdit?.fitBandTemplateId ?? undefined
            : undefined,
        virtualRecruiterEnabled: positionDataToEdit?.virtualRecruiterEnabled
          ? positionDataToEdit?.virtualRecruiterEnabled
          : false,
        modernHirePassThreshold: positionDataToEdit?.modernHirePassThreshold,
        modernHireTemplateId: positionDataToEdit?.modernHireTemplateId,
        automationConfiguration: positionDataToEdit.automationConfiguration,
        languageEvaluationTemplates: positionDataToEdit?.languageEvaluationTemplates,
        leTemplateId: positionDataToEdit?.leTemplateId,
        languageEvaluationEnable:
          !_isEmpty(positionDataToEdit?.leTemplateId) || !_isEmpty(positionDataToEdit?.languageEvaluationTemplates),
        videoUrl: positionDataToEdit?.videoUrl,
        videoUrlThumbnail: positionDataToEdit?.videoUrlThumbnail,
        videoUrlId: positionDataToEdit?.videoUrlId,
        languageEvaluationPassThreshold:
          !_isNil(positionDataToEdit.languageEvaluationTemplates) || !_isEmpty(positionDataToEdit.leTemplateId)
            ? positionDataToEdit?.languageEvaluationPassThreshold || DEFAULT_LANGUAGE_EVALUATION_PASS_THRESHOLD
            : undefined,
        enableScreeningOptions: positionDataToEdit?.enableScreeningOptions ?? false,
        screeningOptions: positionDataToEdit?.enableScreeningOptions
          ? positionDataToEdit?.screeningOptions ?? initialBackgroundCheck
          : initialBackgroundCheck,
        newAvailabilities: positionDataToEdit?.newAvailabilities,
        offerConditions: cleanedOfferConditions,
        i9Enabled: positionDataToEdit?.i9Enabled,
        automateHiringAfterOffer: positionDataToEdit?.automateHiringAfterOffer,
        shouldOverlap: positionDataToEdit?.shouldOverlap,
        maxLimitInterviews: positionDataToEdit?.maxLimitInterviews,
        requisitionActiveThresholdDays: availableRequisitionActiveThresholdDays,
        questionTemplateId: positionDataToEdit?.questionTemplateId || null,
        positionId: isCopyingPosition ? '' : positionDataToEdit?.positionId || '',
        dialogFlowAgents: positionDataToEdit?.dialogFlowEnabled
          ? positionDataToEdit?.dialogFlowAgents?.slice(0, 1) ?? []
          : undefined,
        dialogFlowEnabled: Boolean(positionDataToEdit?.dialogFlowEnabled),
      }));
    } else {
      setJobPositionData(initialJobPositionData);
      if (selectedBusiness) {
        setJobPositionData((prevData) => ({
          ...prevData,
          business: selectedBusiness.id,
          businessName: selectedBusiness.name,
          businessAddress: selectedBusiness.completeAddress,
          languageEvaluationPassThreshold:
            !_isNil(jobPositionData.languageEvaluationTemplates) || !_isEmpty(jobPositionData.leTemplateId)
              ? positionDataToEdit?.languageEvaluationPassThreshold || DEFAULT_LANGUAGE_EVALUATION_PASS_THRESHOLD
              : undefined,
          requisitionActiveThresholdDays: accountRequisitionActiveThresholdDays,
        }));
      }
    }
    if (accounts?.automated) {
      setJobPositionData((prevData) => ({
        ...prevData,
        automationType: AutomationTypes.SCHEDULING,
        automated: true,
      }));
    }

    setFormErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobList, positionDataToEdit]);

  const onOpenReviewModal = () => {
    const offerConditions = jobPositionData?.offerConditions;
    if (
      offerConditions &&
      isObject(offerConditions) &&
      _isEmpty(offerConditions?.en) &&
      (!_isEmpty(offerConditions?.fr) || !_isEmpty(offerConditions?.es))
    ) {
      setIsEnglishOfferEmpty(true);
      return;
    }
    const jobSchema = isSmbRecruiter ? smbJobDetailSchema : jobDetailSchema(positionDataToEdit);
    // const validate = formValidation(jobPositionData, isSmbRecruiter ? smbJobDetailSchema : jobDetailSchema());
    const validate = formValidation(jobPositionData, jobSchema);
    if (validate) {
      checkIfComplianceModalNeeded(onOpenReview);
    }
  };

  const reOrderedMultipleChoiceQuestions = (orderedMultipleChoiceQuestions: MultipleChoiceQuestion[]) => {
    setJobPositionData((prevData) => ({
      ...prevData,
      multipleChoiceQuestions: orderedMultipleChoiceQuestions,
    }));
  };

  const handleQuestionTemplateChange = (template: QuestionnaireTemplateListResponse | undefined) => {
    setJobPositionData((prev) => ({
      ...prev,
      questionTemplateId: template?.id ?? undefined,
    }));
    setSelectedQuestionTemplate(template ?? undefined);
  };

  const reOrderSpecificQuestions = (orderedSpecificQuestions: Question[]) => {
    setJobPositionData((prevData) => ({
      ...prevData,
      specificQuestions: orderedSpecificQuestions,
    }));
  };

  const reOrderFreeFormQuestions = (orderedFreeFormQuestions: FreeFormQuestion[]) => {
    setJobPositionData((prevData) => ({
      ...prevData,
      freeFormQuestions: orderedFreeFormQuestions,
    }));
  };

  const handleEnableCertnBackgroundCheck = (checked: boolean) => {
    setFormErrors(clearErrorMessage(formErrors, 'screeningOptions'));
    setJobPositionData((prevData) => ({
      ...prevData,
      enableScreeningOptions: checked,
    }));
  };

  const setBackgroundCheckValue = (checked: boolean, field: string) => {
    setFormErrors(clearErrorMessage(formErrors, 'screeningOptions'));
    if (!_isNil(screeningOptions)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      let selectedValues: BackgroundChecks = { ...jobPositionData.screeningOptions! };

      if (field === 'request_criminal_record_check') {
        selectedValues = {
          ...selectedValues,
          request_enhanced_identity_verification: true,
          request_enhanced_criminal_record_check: false,
        };
      } else if (field === 'request_enhanced_criminal_record_check') {
        selectedValues = {
          ...selectedValues,
          request_enhanced_identity_verification: true,
          request_criminal_record_check: false,
        };
      } else if (field === 'request_enhanced_identity_verification') {
        selectedValues = {
          ...selectedValues,
          request_criminal_record_check: false,
          request_enhanced_criminal_record_check: false,
        };
      } else if (field === 'request_us_criminal_record_check_tier_1') {
        selectedValues = {
          ...selectedValues,
          request_us_criminal_record_check_tier_2: false,
        };
      } else if (field === 'request_us_criminal_record_check_tier_2') {
        selectedValues = {
          ...selectedValues,
          request_us_criminal_record_check_tier_1: false,
        };
      }

      selectedValues = {
        ...selectedValues,
        [field]: checked,
      };

      setJobPositionData((prevData) => ({
        ...prevData,
        screeningOptions: selectedValues,
      }));
    }
  };

  const onAddVideoToPosition = (
    videoUrlId: string | undefined,
    newVideoUrl: string | undefined,
    videoUrlThumbnail: string | undefined,
  ) => {
    setJobPositionData((prevData) => ({
      ...prevData,
      videoUrl: newVideoUrl,
      videoUrlId,
      videoUrlThumbnail,
    }));
  };

  const showDetailedPositionCreationFields = () => (
    <Fragment>
      {enableMultipleChoiceFeature ? (
        <Fragment>
          <QuestionsView
            jobPositionData={jobPositionData}
            deleteQuestion={deleteQuestion}
            addQuestion={addQuestion}
            showQuestionForm={showFreeFormQuestion}
            addFreeFormQuestion={addFreeFormQuestion}
            addMultipleChoiceQuestion={addMultipleChoiceQuestion}
            deleteFreeFormQuestion={deleteFreeFormQuestion}
            deleteMultipleQuestionsQuestion={deleteMultipleQuestionsQuestion}
            reOrderedMultipleChoiceQuestions={reOrderedMultipleChoiceQuestions}
            reOrderSpecificQuestions={reOrderSpecificQuestions}
            reOrderFreeFormQuestions={reOrderFreeFormQuestions}
            handleQuestionTemplateChange={handleQuestionTemplateChange}
            isEditMode={!_isNil(positionDataToEdit)}
            isCopyingPosition={isCopyingPosition}
            selectedQuestionTemplate={selectedQuestionTemplate ?? undefined}
            scrollContainerRef={modalBodyRef}
          />
        </Fragment>
      ) : (
        <Fragment>
          <Box mb={showFreeFormQuestion ? 0 : 6}>
            <SpecificQuestionsCard
              jobPositionData={jobPositionData}
              deleteQuestion={deleteQuestion}
              addQuestion={addQuestion}
              showQuestionForm={false}
              questionToEdit={initialQuestion}
              questionToEditMode={undefined}
              onRefreshState={() => {}}
            />
          </Box>
          {showFreeFormQuestion && (
            <FreeFormQuestionsCard
              jobPositionData={jobPositionData}
              deleteFreeFormQuestion={deleteFreeFormQuestion}
              addFreeFormQuestion={addFreeFormQuestion}
              showQuestionForm={false}
              questionToEdit={initialFreeFormQuestion}
              questionToEditMode={undefined}
              onRefreshState={() => {}}
            />
          )}
        </Fragment>
      )}
      {accounts?.subscription?.automation && (
        <Fragment>
          <AutomationCard
            jobPositionData={jobPositionData}
            formErrors={formErrors}
            isInterviewAndBusinessLocationSame={isInterviewAndBusinessLocationSame}
            handleChange={handleChange}
            handleAutomationChange={handleAutomationChange}
            handleChangeInterviewLocation={handleChangeInterviewLocation}
            removePadding
          />
        </Fragment>
      )}
      {jobPositionData.automationType !== AutomationTypes.SCHEDULING && (
        <Fragment>
          <InterviewCard
            jobPositionData={jobPositionData}
            formErrors={formErrors}
            isInterviewAndBusinessLocationSame={isInterviewAndBusinessLocationSame}
            handleChange={handleChange}
            handleChangeInterviewLocation={handleChangeInterviewLocation}
          />
        </Fragment>
      )}
      {isCertnBackgroundEnabled && (
        <Fragment>
          <CertnBackgroundCheckCard
            jobPositionData={jobPositionData}
            formErrors={formErrors}
            setBackgroundCheckValue={setBackgroundCheckValue}
            screeningOptions={screeningOptions}
            setEnableCertnBackgroundCheck={handleEnableCertnBackgroundCheck}
          />
        </Fragment>
      )}

      {isOverviewVideoFeatureEnabled && (
        <Fragment>
          <VideoDetailCard
            positionId={_isNil(positionDataToEdit) ? null : positionDataToEdit.id}
            changeOverviewVideo={(fileToUpload: File | null, thumbnail?: File) => {
              setOverviewVideoFile(fileToUpload);
              if (thumbnail) setOverviewThumbnailFile(thumbnail);
            }}
            saveVideoUrl={(url: string | null) => {
              setVideoUrl(url);
            }}
            jobPositionData={jobPositionData}
            onAddVideoToPosition={onAddVideoToPosition}
          />
        </Fragment>
      )}

      <AdvanceFieldsCard
        jobPositionData={jobPositionData}
        setJobPositionData={setJobPositionData}
        jobList={jobList}
        handleSkillChange={handleSkillChange}
        isEnglishOfferEmpty={isEnglishOfferEmpty}
        setIsEnglishOfferEmpty={setIsEnglishOfferEmpty}
      />
      <PositionAvailabilityCard
        jobPositionData={jobPositionData}
        handleAvailabilityChange={handleAvailabilityChange}
        handleChange={handleChange}
      />
    </Fragment>
  );

  const positionCreationBodyContent = () => (
    <Box css={createJobPositionStyle} ref={jobPositionRef}>
      <Fragment>
        <JobPositionDetailCard
          jobPositionData={jobPositionData}
          industries={industries}
          occupationList={occupationList}
          jobList={jobList}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
          salaryErrors={salaryError}
          positionDataToEdit={!!positionDataToEdit}
          handleJobChange={handleJobChange}
          handleChange={handleChange}
          handleSalaryChange={handleSalaryChange}
          handleLanguageEvaluationPhrase={handleLanguageEvaluationPhrase}
          handleLanguageEvaluation={handleLanguageEvaluation}
          handleLanguageChange={handleLanguageChange}
          handleDialogFlowAgents={handleDialogFlowAgents}
          audioEvaluationOptions={audioEvaluationOptions}
          handleDialogFlowCheckbox={handleDialogFlowCheckbox}
          isSmbRecruiter={isSmbRecruiter}
          setJobPositionData={setJobPositionData}
          isCopyPostion={Boolean(isCopyingPosition)}
          LETemplateUsed={isTemplateUsed}
          setLETemplateUsed={setIsTemplateUsed}
        />

        {isSmbRecruiter && showPositionCreationOnPage && (
          <Box width="100%" display="flex" justifyContent="flex-end" pb={6} pt={6} borderTop="1px solid #eee">
            <Tooltip
              label={`${t('jobPosition:contactToEnableAuthorization', { configurableEmail: contactEmailForAuthorization })}`}
              shouldWrapChildren
              isDisabled={!disablePositionCreation}
            >
              <Button
                colorScheme="blue"
                onClick={() => onSmbPositionSave()}
                disabled={isLoading || disablePositionCreation}
                isLoading={isLoading}
              >
                {t('createJobPosition:next')}
              </Button>
            </Tooltip>
          </Box>
        )}
      </Fragment>

      {!isSmbRecruiter && showDetailedPositionCreationFields()}
      {!isSmbRecruiter && (
        <PositionParametersView
          jobPositionData={jobPositionData}
          setJobPositionData={setJobPositionData}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
        />
      )}
      <Box />
    </Box>
  );

  const loadSubmitLabel = () => {
    if (isCopyingPosition) {
      return t('createJobPosition:copyPosition');
    }
    if (_isNil(positionDataToEdit)) {
      return t('createJobPosition:stepsNav.submit');
    }

    return t('createJobPosition:stepsNav.update');
  };

  return (
    <>
      {isReviewOpened && (
        <ReviewCreateJobView
          isNewPosition={_isNil(positionDataToEdit)}
          onClose={() => onCloseReview()}
          onSave={() => saveJobPosition()}
          reviewPosition={isReviewOpened}
          jobPositionData={jobPositionData}
          occupationList={occupationList}
          jobList={jobList}
          isCopyPostion={Boolean(isCopyingPosition)}
          videoUrl={videoUrl}
          isVideoFeatureEnabled={isOverviewVideoFeatureEnabled}
          audioEvaluationOptions={audioEvaluationOptions}
        />
      )}
      {((!isReviewOpened && !isSmbRecruiter) || !showPositionCreationOnPage) && (
        <Modal
          closeOnOverlayClick={false}
          isOpen={createNewPosition}
          size="6xl"
          scrollBehavior="inside"
          onClose={() => onClose(null)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalBody ref={modalBodyRef}>{positionCreationBodyContent()}</ModalBody>
            <ModalFooter justifyContent={isCopyingPosition ? 'space-between' : 'end'}>
              {isCopyingPosition && (
                <Text fontWeight="normal" mb="1rem" py="1" fontSize="14px" fontStyle="italic" display="flex" alignItems="center">
                  <BiInfoCircle style={{ marginRight: '5px' }} />
                  {isSmbRecruiter ? t('createJobPosition:copyInfo') : t('createJobPosition:dublicationInfo')}
                </Text>
              )}
              <Box>
                <Button colorScheme="red" mr={3} onClick={() => onClose(null)} disabled={isLoading}>
                  {t('common:button.cancel')}
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={() => (isSmbRecruiter ? onSmbPositionSave() : onOpenReviewModal())}
                  isLoading={isLoading}
                  alignSelf="right"
                >
                  {isSmbRecruiter ? loadSubmitLabel() : t('createJobPosition:reviewPositionBtn')}
                </Button>
              </Box>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {!isReviewOpened && isSmbRecruiter && showPositionCreationOnPage && <Box>{positionCreationBodyContent()}</Box>}

      {_isNil(positionComplianceCheckedDate) && (
        <PositionComplianceReminder
          isOpen={isComplianceModalOpened}
          onClose={onCloseComplianceModal}
          context={ComplianceContext.POSITION}
          onConfirm={() => (isSmbRecruiter ? saveJobPosition() : onOpenReview())}
        />
      )}
    </>
  );
};
